import { IconName } from '~/components/Icon'

type BaseItem = {
  name: string
}

export type SimpleItem = BaseItem & {
  type?: 'item'
  to: string
  notifications?: number
  releaseStage?: 'beta'
  show?: boolean
  /** Pubstack staff only: a Node page, shown to staff on a Node scope. Drawn with a shield. */
  staffOnly?: boolean
}

export type GroupItem = BaseItem & {
  type: 'group'
  items: SimpleItem[]
}
export type Item = SimpleItem | GroupItem

export type Section = {
  title: string
  icon: IconName
  items: Item[]
}

/**
 * Every monetisation entry, identical for every scope and every role.
 *
 * Nothing here branches on what the scope owns — but that does not mean every entry survives. `Menu`
 * filters this list through `canAccessRoute`, so route metadata decides, and it decides three ways:
 *
 * - **Pitch.** Ad units, Media & stacks and the Catalog declare no `feature`, so they stay on screen
 *   for everyone and the page itself makes the offer — the same card over both, as a modal. Three
 *   entries carry the whole cross-sell, because you cannot sell what you do not show and a sidebar of
 *   locked rows sells nothing.
 * - **Gate.** Everything else disappears on a scope that does not own the product behind it: the
 *   `/monetization/one/*` entries on `feature: 'adstack'`, and Bidder rules, Floor price, My bidder
 *   seats and Deployment center on either product. An Analytics-only scope is left with the three
 *   pitch entries above and no Rules group at all — `Menu` drops a group whose items all filtered
 *   out. Deliberate: those pages are tools for a stack, and a scope with no stack has nothing to
 *   rule on, no seat to manage and nothing to deploy.
 * - **Role.** A Pubstack-only page like Spark disappears for customers, without this file knowing
 *   anything about roles.
 *
 * One and Node still have their own copies of several pages, so several labels appear twice. That
 * duplication is deliberate for now and gets resolved a page at a time, the way Ad units just was.
 */
const getMonetisationItems = (): Item[] => [
  {
    type: 'group',
    name: 'Inventory',
    items: [
      {
        // One entry, four tabs: One's web inventory plus Node's server-side, in-app and AMP.
        name: 'Ad units',
        to: '/monetization/inventory/adunits',
      },
      {
        name: 'Contexts',
        to: '/monetization/one/context/contexts',
      },
      {
        // One list for every media, whichever product it runs on.
        name: 'Media & stacks',
        to: '/monetization/inventory/medias',
      },
    ],
  },
  {
    type: 'group',
    name: 'Rules',
    items: [
      {
        name: 'Bidder',
        to: '/monetization/inventory/bidder-rules',
      },
      {
        name: 'Lazy loading',
        to: '/monetization/one/lazy-loading',
      },
      {
        name: 'Floor price',
        to: '/monetization/inventory/floor-price',
      },
      {
        name: 'Refresh',
        to: '/monetization/one/refresh',
      },
    ],
  },
  {
    type: 'group',
    name: 'Integrations',
    items: [
      {
        name: 'Catalog',
        to: '/monetization/inventory/catalog',
      },
      {
        name: 'My bidder seats',
        to: '/monetization/inventory/my-bidder-seats',
      },
      {
        name: 'Modules',
        to: '/monetization/one/integrations/modules',
      },
      {
        name: 'Wrappers & Ad server',
        to: '/monetization/one/integrations/prebid',
      },
      {
        name: 'Marketplace',
        to: '/monetization/one/integrations/marketplace',
      },
    ],
  },
  {
    // One entry, where there used to be two with the identical name: the page they pointed at was the
    // same component behind two product prefixes.
    name: 'Deployment center',
    to: '/monetization/inventory/deployment-center',
  },
]

const AD_MANAGEMENT = 'Ad Management'

/**
 * No `user` parameter, on purpose: the sidebar is now the same for everyone, so there is nowhere in
 * here to make it differ. Role and entitlement are applied by `Menu`, from route metadata.
 */
export const buildSections = ({ alertsNotification, sparkForPubUiEnabled = false }: { alertsNotification: number; sparkForPubUiEnabled?: boolean }): Section[] => {
  const sections: Section[] = []

  sections.push({
    title: 'Analytics',
    icon: 'chart_bar',
    items: [
      {
        type: 'group',
        name: 'General',
        items: [
          {
            name: 'Overview',
            to: '/analytics/overview',
          },
          {
            name: 'Explore',
            to: '/analytics/explore',
          },
          {
            name: 'Bidders',
            to: '/analytics/bidders',
          },
          {
            name: 'Sites monitoring',
            to: '/analytics/sites',
          },
          {
            name: 'Viewability',
            to: '/analytics/viewability',
          },
          {
            name: 'User sessions',
            to: '/analytics/user-session',
          },
          {
            name: 'Core Web Vitals',
            to: '/analytics/core-web-vitals',
          },
          {
            name: 'Refresh',
            to: '/analytics/refresh',
          },
          {
            name: 'Server-side dashboard',
            to: '/analytics/server-side',
          },
          {
            name: 'Spark for Publishers',
            to: '/analytics/spark-for-pub',
            show: sparkForPubUiEnabled,
          },
        ],
      },
      {
        type: 'group',
        name: 'Ad server',
        items: [
          {
            name: 'Web overview',
            to: '/analytics/gam-overview-web',
          },
          {
            name: 'App overview',
            to: '/analytics/gam-overview-app',
          },
          {
            name: 'AMP overview',
            to: '/analytics/gam-overview-amp',
          },
        ],
      },
      {
        name: 'Reports',
        to: '/reports',
      },
      {
        name: 'Alerts',
        to: '/monitors/summary',
        notifications: alertsNotification,
      },
    ],
  })

  // One section, the same for a Pubstack admin, a One customer, a Node customer and a scope with
  // neither. What the scope owns decides what each page shows, not whether the entry exists.
  sections.push({
    title: AD_MANAGEMENT,
    icon: 'build',
    items: getMonetisationItems(),
  })

  return sections
}
